import React, { useState, useEffect, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import { BubbleMenu } from '@tiptap/react/menus';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TaskListe from './taskListe';
import { apiFetch } from './api';

const ProjektDetail = ({ projektId, onZurueck }) => {
  const [name, setName] = useState('');
  const [beschreibung, setBeschreibung] = useState('');
  const [status, setStatus] = useState('offen');
  const [prioritaet, setPrioritaet] = useState('mittel');
  const [deadline, setDeadline] = useState('');
  const [mutterFarbe, setMutterFarbe] = useState(null);
  const [mutterName, setMutterName] = useState('');
  const [loading, setLoading] = useState(true);
  const [gespeichert, setGespeichert] = useState(false);
  const [alleUsers, setAlleUsers] = useState([]);
  const [members, setMembers] = useState([]);

  const nameRef = useRef('');
  const beschreibungRef = useRef('');
  const statusRef = useRef('offen');
  const prioRef = useRef('mittel');
  const deadlineRef = useRef('');
  useEffect(() => { nameRef.current = name; }, [name]);
  useEffect(() => { beschreibungRef.current = beschreibung; }, [beschreibung]);
  useEffect(() => { statusRef.current = status; }, [status]);
  useEffect(() => { prioRef.current = prioritaet; }, [prioritaet]);
  useEffect(() => { deadlineRef.current = deadline; }, [deadline]);

  const editor = useEditor({
    extensions: [StarterKit, Underline],
    content: '',
    onUpdate: ({ editor }) => { speichern({ notizen: editor.getHTML() }); }
  });

  const speichern = async ({ notizen } = {}) => {
    await apiFetch(`/projekte/${projektId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: nameRef.current,
        beschreibung: beschreibungRef.current,
        status: statusRef.current,
        prioritaet: prioRef.current,
        deadline: deadlineRef.current,
        notizen: notizen !== undefined ? notizen : (editor ? editor.getHTML() : '')
      })
    });
    setGespeichert(true);
    setTimeout(() => setGespeichert(false), 2000);
  };

  const ladeMembers = async () => {
    const res = await apiFetch(`/projekte/${projektId}`);
    const data = await res.json();
    setMembers(data.members || []);
  };

  useEffect(() => {
    const ladeAlles = async () => {
      const [itemRes, usersRes] = await Promise.all([
        apiFetch(`/projekte/${projektId}`),
        apiFetch('/users')
      ]);
      const data = await itemRes.json();
      const users = await usersRes.json();
      setName(data.name);
      setBeschreibung(data.beschreibung || '');
      setStatus(data.status || 'offen');
      setPrioritaet(data.prioritaet || 'mittel');
      setDeadline(data.deadline || '');
      setMutterFarbe(data.mutter_farbe || null);
      setMutterName(data.mutter_name || '');
      nameRef.current = data.name;
      beschreibungRef.current = data.beschreibung || '';
      statusRef.current = data.status || 'offen';
      prioRef.current = data.prioritaet || 'mittel';
      deadlineRef.current = data.deadline || '';
      setMembers(data.members || []);
      setAlleUsers(users);
      setLoading(false);
      if (editor) editor.commands.setContent(data.notizen || '');
    };
    if (projektId) ladeAlles();
  }, [projektId, editor]);

  const personHinzufuegen = async (userId, rolle) => {
    await apiFetch(`/projekte/${projektId}/members`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: userId, rolle })
    });
    ladeMembers();
  };

  const personEntfernen = async (userId) => {
    await apiFetch(`/projekte/${projektId}/members/${userId}`, { method: 'DELETE' });
    ladeMembers();
  };

  const feldAendern = async (setter, ref, wert) => {
    setter(wert);
    ref.current = wert;
    await speichern();
  };

  const projektLoeschen = async () => {
    await apiFetch(`/projekte/${projektId}`, { method: 'DELETE' });
    onZurueck();
  };

  if (loading) return <div style={{ padding: '40px', color: '#9b9b9b', fontSize: '14px' }}>Lädt...</div>;

  const verantwortliche = members.filter(m => m.rolle === 'verantwortlich');
  const mitarbeiter = members.filter(m => m.rolle === 'mitarbeiter');
  const zugewieseneIds = members.map(m => m.id);
  const verfuegbar = alleUsers.filter(u => !zugewieseneIds.includes(u.id));

  const btn = (a) => ({
    minWidth: '30px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '0 8px', backgroundColor: a ? '#f0f0ee' : 'transparent', color: a ? '#1a1a1a' : '#6b6b6b',
    border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '500', cursor: 'pointer'
  });
  const divider = <div style={{ width: '1px', backgroundColor: '#ececec', margin: '5px 3px' }} />;

  const chip = (m) => (
    <div key={m.id} style={{
      display: 'inline-flex', alignItems: 'center', gap: '8px', backgroundColor: '#f7f7f5',
      borderRadius: '20px', padding: '5px 5px 5px 12px', fontSize: '13px'
    }}>
      <div style={{
        width: '22px', height: '22px', borderRadius: '50%', backgroundColor: '#e4e4e1',
        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: '600', color: '#6b6b6b'
      }}>{m.name.charAt(0).toUpperCase()}</div>
      <span style={{ color: '#1a1a1a' }}>{m.name}</span>
      <button onClick={() => personEntfernen(m.id)} style={{
        width: '20px', height: '20px', borderRadius: '50%', border: 'none',
        backgroundColor: 'transparent', color: '#9b9b9b', cursor: 'pointer', fontSize: '14px'
      }}>×</button>
    </div>
  );

  const dropdown = (rolle, disabled) => (
    <select value="" disabled={disabled || verfuegbar.length === 0}
      onChange={(e) => { if (e.target.value) personHinzufuegen(Number(e.target.value), rolle); }}
      style={{
        padding: '6px 12px', fontSize: '13px', border: '1px dashed #d4d4d4', borderRadius: '20px',
        outline: 'none', cursor: disabled ? 'not-allowed' : 'pointer', color: '#6b6b6b',
        backgroundColor: 'transparent', opacity: (disabled || verfuegbar.length === 0) ? 0.4 : 1
      }}>
      <option value="">+ {rolle === 'verantwortlich' ? 'Verantwortlich' : 'Mitarbeiter'}</option>
      {verfuegbar.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
    </select>
  );

  const sektionLabel = (t) => (
    <p style={{ fontSize: '11px', fontWeight: '500', color: '#9b9b9b', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '10px' }}>{t}</p>
  );

  const selectStyle = {
    width: '100%', padding: '8px 12px', fontSize: '13px', border: '1px solid #e8e8e8',
    borderRadius: '8px', outline: 'none', cursor: 'pointer', backgroundColor: '#ffffff', color: '#1a1a1a'
  };

  return (
    <div style={{ position: 'relative' }}>
      {mutterFarbe && (
        <div style={{
          position: 'absolute', left: 0, top: 0, bottom: 0,
          width: '4px', backgroundColor: mutterFarbe, borderRadius: '4px 0 0 4px'
        }} />
      )}

      <div style={{ padding: '40px', paddingLeft: mutterFarbe ? '52px' : '40px' }}>
        {gespeichert && (
          <div style={{
            position: 'fixed', top: '24px', right: '24px', fontSize: '12px', color: '#6b6b6b',
            backgroundColor: '#ffffff', border: '1px solid #efefef', borderRadius: '8px',
            padding: '6px 12px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', zIndex: 100
          }}>✓ Gespeichert</div>
        )}

        <div onClick={onZurueck} style={{
          display: 'inline-flex', alignItems: 'center', gap: '6px', color: '#9b9b9b',
          fontSize: '13px', cursor: 'pointer', marginBottom: '8px'
        }}>← Zurück</div>

        {mutterName && (
          <p style={{
            fontSize: '11px', fontWeight: '500', color: mutterFarbe || '#9b9b9b',
            textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '24px'
          }}>{mutterName}</p>
        )}

        <div style={{ display: 'flex', gap: '48px', alignItems: 'flex-start' }}>
          <div style={{ flex: '1 1 60%', minWidth: 0 }}>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} onBlur={() => speichern()}
              style={{
                width: '100%', fontSize: '28px', fontWeight: '700', color: '#1a1a1a', border: 'none',
                outline: 'none', backgroundColor: 'transparent', marginBottom: '8px', letterSpacing: '-0.5px', boxSizing: 'border-box'
              }} />
            <input type="text" value={beschreibung} onChange={(e) => setBeschreibung(e.target.value)} onBlur={() => speichern()}
              placeholder="Beschreibung hinzufügen..."
              style={{
                width: '100%', fontSize: '14px', color: '#9b9b9b', border: 'none', outline: 'none',
                backgroundColor: 'transparent', marginBottom: '36px', boxSizing: 'border-box'
              }} />

            <div style={{ borderTop: '1px solid #efefef', marginBottom: '24px' }} />
            {sektionLabel('Notizen')}

            <div style={{ fontSize: '14px', color: '#1a1a1a', lineHeight: '1.7', minHeight: '300px' }}>
              <style>{`
                .ProseMirror { outline: none; min-height: 300px; }
                .ProseMirror p { margin: 0 0 8px 0; }
                .ProseMirror p.is-editor-empty:first-child::before { content: 'Notizen schreiben...'; color: #c4c4c4; pointer-events: none; float: left; height: 0; }
                .ProseMirror strong { font-weight: 600; }
                .ProseMirror em { font-style: italic; }
                .ProseMirror u { text-decoration: underline; }
                .ProseMirror h1 { font-size: 24px; font-weight: 700; margin: 18px 0 8px 0; letter-spacing: -0.5px; }
                .ProseMirror h2 { font-size: 19px; font-weight: 600; margin: 14px 0 6px 0; }
                .ProseMirror h3 { font-size: 16px; font-weight: 600; margin: 12px 0 4px 0; }
                .ProseMirror ul, .ProseMirror ol { padding-left: 22px; margin: 8px 0; }
                .ProseMirror li { margin-bottom: 4px; }
              `}</style>
              {editor && (
                <BubbleMenu editor={editor}>
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: '1px', backgroundColor: '#ffffff',
                    border: '1px solid #ececec', borderRadius: '10px', padding: '4px',
                    boxShadow: '0 6px 20px rgba(0,0,0,0.10), 0 1px 3px rgba(0,0,0,0.06)'
                  }}>
                    <button onClick={() => editor.chain().focus().toggleBold().run()} style={btn(editor.isActive('bold'))}><strong>B</strong></button>
                    <button onClick={() => editor.chain().focus().toggleItalic().run()} style={btn(editor.isActive('italic'))}><em style={{ fontFamily: 'Georgia, serif' }}>i</em></button>
                    <button onClick={() => editor.chain().focus().toggleUnderline().run()} style={btn(editor.isActive('underline'))}><span style={{ textDecoration: 'underline' }}>U</span></button>
                    <button onClick={() => editor.chain().focus().toggleStrike().run()} style={btn(editor.isActive('strike'))}><s>S</s></button>
                    {divider}
                    <button onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} style={btn(editor.isActive('heading', { level: 1 }))}>H1</button>
                    <button onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} style={btn(editor.isActive('heading', { level: 2 }))}>H2</button>
                    <button onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} style={btn(editor.isActive('heading', { level: 3 }))}>H3</button>
                    {divider}
                    <button onClick={() => editor.chain().focus().toggleBulletList().run()} style={btn(editor.isActive('bulletList'))}>•</button>
                    <button onClick={() => editor.chain().focus().toggleOrderedList().run()} style={btn(editor.isActive('orderedList'))}>1.</button>
                  </div>
                </BubbleMenu>
              )}
              <EditorContent editor={editor} />
            </div>
          </div>

          <div style={{ flex: '0 0 280px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
            <div>
              {sektionLabel('Status')}
              <select value={status} onChange={(e) => feldAendern(setStatus, statusRef, e.target.value)} style={selectStyle}>
                <option value="offen">Offen</option>
                <option value="in arbeit">In Arbeit</option>
                <option value="erledigt">Erledigt</option>
              </select>
            </div>

            <div>
              {sektionLabel('Priorität')}
              <select value={prioritaet} onChange={(e) => feldAendern(setPrioritaet, prioRef, e.target.value)} style={selectStyle}>
                <option value="niedrig">Niedrig</option>
                <option value="mittel">Mittel</option>
                <option value="hoch">Hoch</option>
              </select>
            </div>

            <div>
              {sektionLabel('Deadline')}
              <input type="date" value={deadline || ''} onChange={(e) => feldAendern(setDeadline, deadlineRef, e.target.value)}
                style={{ ...selectStyle, cursor: 'text' }} />
            </div>

            <div>
              {sektionLabel('Verantwortliche')}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {verantwortliche.map(chip)}
                {dropdown('verantwortlich', verantwortliche.length >= 2)}
              </div>
            </div>

            <div>
              {sektionLabel('Mitarbeiter')}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {mitarbeiter.map(chip)}
                {dropdown('mitarbeiter', false)}
              </div>
            </div>

            <div>
              {sektionLabel('Tasks')}
              <TaskListe projektId={projektId} />
            </div>

            <div style={{ borderTop: '1px solid #efefef', paddingTop: '16px' }}>
              <button onClick={projektLoeschen} style={{
                padding: '6px 14px', backgroundColor: 'transparent', border: '1px solid #ffdddd',
                borderRadius: '6px', fontSize: '12px', color: '#cc0000', cursor: 'pointer'
              }}>Projekt löschen</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjektDetail;